import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Skeleton } from '@mui/material';
import palette from '@/palette';
import { Card } from '@/components/card';
import { TrendIcon } from '@/assests/icons';
import { NumberField } from '@refinedev/mui';
import { SuspenseAndErrorBoundary } from '@/components/security';
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useOne, useParsed } from '@refinedev/core';
import { analyticsOrderData } from '@/utils/data-provider/data';
import { useTheme } from '@mui/material/styles';

export const MonthlyOrdersChart = () => {

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  let { data: responseData, isLoading } = useOne<{
      monthly_sales: {month: number, year: number, order_count: number, total_sales: number}[],
      order_by_status: {count: number, grand_total_amount: number, grand_total_quantity: number, status: string}[]
    }>({
    resource: "analytics",
    id: "orders",
  });

  let data = responseData?.data;
  const params = useParsed();
  if (params.pathname == '/dummy-dashboard') {
    isLoading = false;
    // @ts-ignore
    data = analyticsOrderData;
  }

  // Memoize chart options and series to prevent unnecessary recalculations
  const { chartOptions, series, trendValue, cardTitle } = useMemo(() => {
    if (!data || !data.monthly_sales) {
      return { 
        chartOptions: {}, 
        series: [], 
        trendValue: 0,
        cardTitle: 'Monthly Sales' 
      };
    }
    
    // Ensure we have 6 months of data, filling with empty months if needed
    let monthlyData = [...data.monthly_sales];

    // Sort the data by year and month in ascending order
    monthlyData.sort((a, b) => {
      if (a.year === b.year) {
        return a.month - b.month;
      }
      return a.year - b.year;
    });

    // Use the current month and year as the latest month
    const now = new Date();
    let currentMonth = now.getMonth() + 1; // getMonth() is 0-based
    let currentYear = now.getFullYear();

    // Generate the last 6 months (ending at the current month)
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      let m = currentMonth - i;
      let y = currentYear;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      last6Months.push({ month: m, year: y });
    }
    // For each of these months, find data or fill with empty
    monthlyData = last6Months.map(({ month, year }) => {
      const found = data.monthly_sales.find((d: any) => d.month === month && d.year === year);
      return found || { month, year, order_count: 0, total_sales: 0 };
    });
    
    // Take the last 6 months
    const last6MonthsSales = monthlyData.slice(-6);

    // Calculate trend value (difference between last two months)
    const lastIndex = last6MonthsSales.length - 1;
    const secondLastIndex = lastIndex - 1;
    const trend = lastIndex >= 0 && secondLastIndex >= 0
      ? last6MonthsSales[lastIndex].order_count - last6MonthsSales[secondLastIndex].order_count
      : 0;

    const options: ApexOptions = {
      chart: {
        type: 'area',
        toolbar: { show: false },
        background: 'transparent',
        fontFamily: 'inherit',
        animations: {
          enabled: true,
          speed: 800,
        },
        dropShadow: {
          enabled: true,
          top: 3,
          left: 0,
          blur: 4,
          opacity: 0.1
        },
        zoom: {
          enabled: false
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.6,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      colors: [palette.dark.primary.main, palette.dark.info.main],
      xaxis: {
        type: 'category',
        categories: last6MonthsSales.map((sale: any) => `${sale.month}/${sale.year}`),
        labels: {
          style: {
            fontSize: '12px',
            fontFamily: 'inherit',
            colors: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
          },
          maxHeight: 40,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: {
          show: false
        }
      },
      yaxis: [
        {
          title: {
            text: 'Total Sales (₹)',
            style: {
              fontSize: '12px',
              fontFamily: 'inherit',
              color: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
            }
          },
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: 'inherit',
              colors: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
            },
            formatter: (value) => Math.round(value).toString(),
          },
          tickAmount: 4,
          min: 0,
          forceNiceScale: true,
        },
        {
          opposite: true,
          title: {
            text: 'Order Count',
            style: {
              fontSize: '12px',
              fontFamily: 'inherit',
              color: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
            }
          },
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: 'inherit',
              colors: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
            },
            formatter: (value) => Math.round(value).toString(),
          },
          tickAmount: 4,
          min: 0,
          forceNiceScale: true,
        }
      ],
      grid: {
        show: true,
        borderColor: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
        strokeDashArray: 4,
        padding: {
          top: 5,
          right: 2,
          bottom: 5,
          left: 10
        },
      },
      tooltip: {
        theme: theme.palette.mode,
        shared: true,
        intersect: false,
        y: {
          formatter: (value, { seriesIndex }) => {
            if (seriesIndex === 0) {
              return '₹' + Math.round(value).toLocaleString();
            } else {
              return Math.round(value).toString() + ' orders';
            }
          },
        },
        marker: {
          show: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        fontFamily: 'inherit',
        offsetY: -5,
        labels:{
          colors: isDarkMode ? palette.dark.grey[200] : palette.light.grey[800],
        },
        markers:{
          strokeWidth: 0,
          shape: 'square',
        }
      },
      markers: {
        size: 0,
        strokeWidth: 0,
        hover: {
          size: 7,
        }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          yaxis: {
            labels: {
              style: {
                fontSize: '10px'
              }
            }
          },
          grid: {
            padding: {
              left: 5,
              right: 5
            }
          }
        }
      }]
    };

    const seriesData = [
      {
        name: "Total Sales",
        data: last6MonthsSales.map((sale: any) => sale.total_sales),
        type: 'area'
      },
      {
        name: "Order Count",
        data: last6MonthsSales.map((sale: any) => sale.order_count),
        type: 'area'
      }
    ];

    return { 
      chartOptions: options, 
      series: seriesData, 
      trendValue: trend,
      cardTitle: 'Last 6 Months Sales & Orders'
    };
  }, [data, isDarkMode, theme]);

  return (      
    <Card
      title={cardTitle}
      icon={<ShoppingCartOutlinedIcon />}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '.MuiCardContent-root': {
          flexGrow: 1,
          height: 'auto',
          padding: '16px',
          paddingBottom: '16px !important',
        },
        '&:hover': {
          boxShadow: '0 6px 25px 0 rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)'
        }
      }}
      cardContentProps={{
        sx: {
          height: '208px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      cardHeaderProps={{
        action: !isLoading && data && (
          <TrendIcon
            trend={trendValue}
            text={
              <NumberField
                value={trendValue || 0}
                options={{
                  notation: 'compact',
                }}
              />
            }
          />
        ),
      }}
    >
      <SuspenseAndErrorBoundary>
        {isLoading ? (
          <Skeleton
            variant="rounded"
            height='100%'
            width='100%'
            animation="wave"
          />
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="area"
            height="100%"
            width="100%"
          />
        )}
      </SuspenseAndErrorBoundary>
    </Card>
  );
};